import React, {FC, ReactNode, useRef} from "react";
import {Alert, Button, Divider, Form, Popconfirm, Space, Typography} from "antd";
import {CheckCircleOutlined, DeleteOutlined, DownloadOutlined, PlusOutlined, SendOutlined, UploadOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {ActivityFields} from "./ActivityFields";
import {DynamicAnswerFields} from "./DynamicAnswerFields";
import {PrivacyConsentField} from "./FormFeedback";
import {ParticipantFields} from "./ParticipantFields";
import {ParticipationFeeField, PaymentMethodField} from "./ParticipationFields";
import {PayerFields} from "./PayerFields";
import {
    buildInitialBulkRegistrationRowValues,
    buildPublicBulkRegistrationTemplateUrl,
    filterPublicRegistrationOptionsForParticipationFee
} from "./publicRegistrationModel";
import {useRegistrationCopy} from "./registrationCopy";
import type {
    PayerTypeValue,
    PaymentMethodValue,
    PublicEventFormResponse,
    PublicParticipationFeeOption,
    PublicRegistrationBulkRowValues,
    PublicRegistrationBulkSubmissionResult
} from "./types";

export type BulkImportNotice = {
    type: "success" | "warning" | "error";
    message: string;
    description?: string;
};

const BulkSubmissionResults: FC<{results: PublicRegistrationBulkSubmissionResult[]}> = ({results}) => {
    const {labels} = useRegistrationCopy();

    return (
        <div className="public-registration-bulk-submit-results">
            <Typography.Title level={3}>{labels.bulkSubmitResults}</Typography.Title>
            {results.map(result => (
                <div
                    key={result.rowNumber}
                    className={`public-registration-bulk-submit-result public-registration-bulk-submit-result--${result.status}`}
                >
                    <div>
                        <Typography.Text strong>
                            {labels.bulkRow} {result.rowNumber}: {result.participantName || result.email}
                        </Typography.Text>
                        <Typography.Text type="secondary">{result.email}</Typography.Text>
                    </div>
                    <div>
                        <Typography.Text strong className="public-registration-bulk-submit-status">
                            {result.status === "accepted" ? labels.bulkSubmitAccepted : labels.bulkSubmitRejected}
                        </Typography.Text>
                        {result.status === "accepted" ? (
                            result.verificationTokenExpiresAtUtc ? (
                                <Typography.Text>
                                    {labels.verificationExpires}: {dayjs(result.verificationTokenExpiresAtUtc).format("YYYY-MM-DD HH:mm")}
                                </Typography.Text>
                            ) : null
                        ) : (
                            <Typography.Text>{result.error}</Typography.Text>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

// Group entry: one shared payer/fee/payment setup and one row per participant, with CSV import.
export const BulkRegistrationSection: FC<{
    eventForm: PublicEventFormResponse;
    payerType: PayerTypeValue;
    requiresParticipationFee: boolean;
    selectedParticipationFee?: PublicParticipationFeeOption;
    showParticipationPayment: boolean;
    participationMethods: PaymentMethodValue[];
    rows?: PublicRegistrationBulkRowValues[];
    importNotice: BulkImportNotice | null;
    onImportCsv: (file?: File | null) => void;
    validationNotice: string | null;
    submissionResults: PublicRegistrationBulkSubmissionResult[];
    submitErrorAlert: ReactNode;
    submitting: boolean;
    onValidate: () => void;
    onSubmit: () => void;
}> = ({
    eventForm,
    payerType,
    requiresParticipationFee,
    selectedParticipationFee,
    showParticipationPayment,
    participationMethods,
    rows,
    importNotice,
    onImportCsv,
    validationNotice,
    submissionResults,
    submitErrorAlert,
    submitting,
    onValidate,
    onSubmit
}) => {
    const {labels} = useRegistrationCopy();
    const csvFileInputRef = useRef<HTMLInputElement | null>(null);
    const fields = eventForm.template?.fields ?? [];

    const onCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onImportCsv(event.target.files?.[0]);
        event.target.value = "";
    };

    return (
        <>
            <Alert
                type="info"
                showIcon
                message={labels.bulkScopeTitle}
                description={labels.bulkScopeNotice}
            />

            {selectedParticipationFee?.requiresParticipantTypeProof === true ? (
                <Alert
                    type="warning"
                    showIcon
                    message={labels.bulkProofNotSupported}
                />
            ) : null}

            <section className="public-registration-form-section">
                <Typography.Title level={2}>{labels.bulkSharedSettings}</Typography.Title>
                <Typography.Paragraph>{labels.bulkSharedSettingsHelp}</Typography.Paragraph>

                <PayerFields payerType={payerType}/>

                {requiresParticipationFee ? (
                    <>
                        <Divider/>

                        <Typography.Title level={3}>{labels.participationFee}</Typography.Title>
                        <Typography.Paragraph>{labels.itemsHelp}</Typography.Paragraph>
                        <ParticipationFeeField fees={eventForm.participationFees ?? []}/>
                    </>
                ) : null}

                {showParticipationPayment
                    ? <PaymentMethodField name="participationPaymentMethod" methods={participationMethods}/>
                    : null}
            </section>

            <section className="public-registration-form-section">
                <Typography.Title level={2}>{labels.bulkParticipantRows}</Typography.Title>
                <Typography.Paragraph>{labels.bulkParticipantRowsHelp}</Typography.Paragraph>

                <div className="public-registration-bulk-csv-actions">
                    <Typography.Text strong>{labels.bulkCsvTools}</Typography.Text>
                    <Space wrap>
                        <Button
                            icon={<DownloadOutlined/>}
                            href={buildPublicBulkRegistrationTemplateUrl(eventForm.eventId)}
                            download
                        >
                            {labels.downloadBulkCsvTemplate}
                        </Button>
                        <Button
                            icon={<UploadOutlined/>}
                            onClick={() => csvFileInputRef.current?.click()}
                        >
                            {labels.importBulkCsv}
                        </Button>
                    </Space>
                    <input
                        ref={csvFileInputRef}
                        className="public-registration-hidden-file-input"
                        type="file"
                        accept=".csv,text/csv"
                        onChange={onCsvFileChange}
                    />
                </div>

                {importNotice ? (
                    <Alert
                        type={importNotice.type}
                        showIcon
                        message={importNotice.message}
                        description={importNotice.description}
                    />
                ) : null}

                <Form.List name="bulkRows">
                    {(listFields, {add, remove}) => (
                        <div className="public-registration-bulk-rows">
                            {listFields.map((field, index) => {
                                const row = rows?.[index];
                                const rowParticipantName = [row?.firstName, row?.lastName]
                                    .filter(Boolean)
                                    .join(" ") || `${labels.bulkRow} ${index + 1}`;
                                const rowRegistrationOptions = filterPublicRegistrationOptionsForParticipationFee(
                                    eventForm.registrationOptions,
                                    selectedParticipationFee,
                                    row?.selectedOptionIds ?? []);

                                return (
                                    <div className="public-registration-bulk-row" key={field.key}>
                                        <div className="public-registration-bulk-row-header">
                                            <Typography.Title level={3}>
                                                {labels.bulkRow} {index + 1}
                                            </Typography.Title>
                                            {listFields.length > 1 ? (
                                                <Popconfirm
                                                    title={labels.deleteConfirmationTitle}
                                                    description={`${labels.deleteBulkRowTarget} "${rowParticipantName}". ${labels.deleteConfirmationQuestion}`}
                                                    okText={labels.removeBulkRow}
                                                    cancelText={labels.deleteConfirmationCancel}
                                                    okButtonProps={{danger: true}}
                                                    onConfirm={() => remove(field.name)}
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined/>}
                                                        aria-label={`${labels.removeBulkRow} ${index + 1}`}
                                                    />
                                                </Popconfirm>
                                            ) : null}
                                        </div>

                                        <ParticipantFields namePrefix={[field.name]}/>

                                        {fields.length > 0 ? (
                                            <DynamicAnswerFields fields={fields} namePrefix={[field.name, "answers"]}/>
                                        ) : null}

                                        {rowRegistrationOptions.length > 0 ? (
                                            <div className="public-registration-bulk-row-activities">
                                                <Divider/>
                                                <Typography.Title level={4}>{labels.additionalOptions}</Typography.Title>
                                                <Typography.Paragraph>{labels.itemsHelp}</Typography.Paragraph>
                                                <ActivityFields
                                                    options={rowRegistrationOptions}
                                                    selectedOptionIdsName={[field.name, "selectedOptionIds"]}
                                                    activityAnswersName={[field.name, "activityAnswers"]}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}

                            <Button
                                icon={<PlusOutlined/>}
                                onClick={() => add(buildInitialBulkRegistrationRowValues(
                                    eventForm.template?.fields,
                                    eventForm.registrationOptions))}
                            >
                                {labels.addBulkRow}
                            </Button>
                        </div>
                    )}
                </Form.List>

                {submitErrorAlert}

                {validationNotice ? (
                    <Alert type="success" showIcon message={validationNotice}/>
                ) : null}

                {submissionResults.length > 0 ? <BulkSubmissionResults results={submissionResults}/> : null}

                <PrivacyConsentField/>

                <div className="public-registration-submit-row public-registration-submit-row--split">
                    <Space wrap>
                        <Button
                            icon={<CheckCircleOutlined/>}
                            disabled={submitting}
                            onClick={onValidate}
                        >
                            {labels.validateBulkRows}
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined/>}
                            loading={submitting}
                            onClick={onSubmit}
                        >
                            {labels.submitBulkRows}
                        </Button>
                    </Space>
                </div>
            </section>
        </>
    );
};
